// src/App.js
import React, { useContext, useState, useEffect, Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import { WalletContext } from './contexts/WalletContext';
import { AuthContext } from './contexts/AuthContext';
import { SettingsContext } from './contexts/SettingsContext';
import { NetworkContext } from './contexts/NetworkContext';
import { handleTelegramParams, initTelegramWebApp, isTelegramWebApp, handleUniversalDeepLink } from './utils/deepLinkUtils';
import AuthWrapper from './components/Auth/AuthWrapper';
import WelcomePage from './pages/Welcome/WelcomePage';
import Wallet from './pages/Wallet/Wallet';
import Swap from './pages/Swap/Swap';
import Browser from './pages/Browser/BrowserPage'; 
import Settings from './pages/Settings/Settings';
import Transactions from './pages/Transactions/Transactions';
import WalletLoading from './components/WalletLoading';
import { ChainParamRedirect, TokenParamRedirect } from './components/DeepLinks/DeepLinkRedirects';
import TabBar from './components/TabBar';
import { InstallPWABanner } from './components/InstallPWA'; 
import { debugAuthentication } from './utils/debugUtils';
import { useTranslation } from 'react-i18next'; 
import RequestModalController from './components/RequestModalController'; // Add this import
import './globalStyles.css';
import './theme.css';
import './i18n';

// Lazy load components with specific fallbacks to prevent message flashing
const ChainRedirect = lazy(() => {
  // Add a small delay to ensure proper loading and message display
  return new Promise(resolve => {
    // Preload the component
    const componentPromise = import('./components/DeepLinks/ChainRedirect');
    // Resolve after ensuring both the component is loaded and a minimum time has passed
    setTimeout(() => resolve(componentPromise), 100);
  });
});

const SwapRedirect = lazy(() => {
  // Add a small delay to ensure proper loading and message display
  return new Promise(resolve => {
    // Preload the component
    const componentPromise = import('./components/DeepLinks/SwapRedirect');
    // Resolve after ensuring both the component is loaded and a minimum time has passed
    setTimeout(() => resolve(componentPromise), 100);
  });
});

// Lazy load the TelegramSwapRedirect component
const TelegramSwapRedirect = lazy(() => {
  return new Promise(resolve => {
    const componentPromise = import('./components/DeepLinks/TelegramSwapRedirect');
    setTimeout(() => resolve(componentPromise), 100);
  });
});

// Lazy load the InstallPWAGuide component
const InstallPWAGuide = lazy(() => import('./components/InstallPWA/InstallPWAGuide'));

// Create specific fallbacks for different routes
const ChainFallback = () => {
  const { t } = useTranslation();
  return <WalletLoading message={t('deepLinks.chainRedirect.loading')} minLoadTime={1000} />;
};

const SwapFallback = () => {
  const { t } = useTranslation();
  return <WalletLoading message={t('deepLinks.swapRedirect.loading')} minLoadTime={1000} />;
};

const WelcomeFallback = () => {
  const { t } = useTranslation();
  return <WalletLoading message={t('welcome.steps.welcomeIntro')} minLoadTime={1000} />;
};

// This component holds all tab pages but only displays the active one based on URL
const TabContainer = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('wallet');
  const [deepLinkTokenData, setDeepLinkTokenData] = useState(null);
  
  // Add these context usages
  const { userWallets, isDecrypting } = useContext(WalletContext);
  const { isAuthenticated } = useContext(AuthContext);
  
  useEffect(() => {
    // Update active tab based on URL
    const path = location.pathname;
    if (path.includes('/swap')) setActiveTab('swap');
    else if (path.includes('/browser')) setActiveTab('browser');
    else if (path.includes('/settings')) setActiveTab('settings');
    else if (path.includes('/transactions')) setActiveTab('transactions');
    else setActiveTab('wallet');
  }, [location.pathname]);

  // Handle deep link swap token when navigating to swap page
  useEffect(() => {
    // Check if we have a deep link token stored in session storage
    const deepLinkSwapData = sessionStorage.getItem('deep_link_swap_token');
    
    if (deepLinkSwapData) {
      try {
        const swapData = JSON.parse(deepLinkSwapData);
        
        if (swapData.token && swapData.action) {
          // Store the data in state to pass to Swap component
          setDeepLinkTokenData(swapData);
          
          // Don't clear the data yet - let the Swap component handle it
          console.log('Found deep link swap configuration:', swapData);
        }
      } catch (error) {
        console.error('Error parsing deep link swap configuration:', error);
        sessionStorage.removeItem('deep_link_swap_token');
      }
    }
  }, [activeTab]); // Re-check when activeTab changes
  
  return (
    <div className="tab-container">
      {/* All tabs are rendered at all times, but only the active one is visible */}
      <div style={{ display: activeTab === 'wallet' ? 'block' : 'none' }}>
        <Wallet />
      </div>
      
      <div style={{ display: activeTab === 'swap' ? 'block' : 'none' }}>
        <Swap deepLinkTokenData={deepLinkTokenData} />
      </div>
      
      <div style={{ display: activeTab === 'browser' ? 'block' : 'none' }}>
        <Browser />
      </div>

      <div style={{ display: activeTab === 'transactions' ? 'block' : 'none' }}>
        <Transactions />
      </div>
      
      <div style={{ display: activeTab === 'settings' ? 'block' : 'none' }}>
        <Settings />
      </div>
      
      {/* Add RequestModalController instances to handle all requests */}
      <RequestModalController origin="wallet" priority={true} />
      <RequestModalController origin="browser" priority={false} />
      
      <TabBar />
      
      {/* Add PWA Installation Banner */}
      <InstallPWABanner />
    </div>
  );
};

// Theme initialization component
const ThemeInitializer = ({ children }) => {
  const { theme } = useContext(SettingsContext);

  // Apply theme when it changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    
    // Also update meta theme-color for mobile devices
    if (theme === 'dark') {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#121212');
    } else {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#f8f9fa');
    }
  }, [theme]);
  
  return <>{children}</>;
};

function App() {
  const { t } = useTranslation(); 
  // We fetch userWallets, isInitialized, and hasStoredWallets from context
  const { userWallets, isInitialized: walletInitialized, hasStoredWallets, isEncrypted } = useContext(WalletContext);
  const { isAuthenticated, isInitialized: authInitialized, passwordDeferred } = useContext(AuthContext);
  const { currentChain, switchNetwork } = useContext(NetworkContext); 

  // Force loading for minimum time
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const MINIMUM_LOADING_TIME = 1000; // 1 second
  
  // Start timer when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, MINIMUM_LOADING_TIME);
    
    return () => clearTimeout(timer);
  }, []);

  // Remove 'ready' which is causing the ESLint error
  const { i18n } = useTranslation();
  
  // Add a fallback mechanism for translation failures
  const safeT = (key, options) => {
    try {
      // Try to get the translation
      const translation = t(key, options);
      
      // If translation is the key itself (missing translation), extract last part as fallback
      if (translation === key) {
        return key.split('.').pop();
      }
      
      return translation;
    } catch (error) {
      console.error(`Translation error for key "${key}":`, error);
      // Return a readable fallback from the key
      return key.split('.').pop();
    }
  };
  
  // State to track if we're in the middle of a chain change
  const [isChangingChain, setIsChangingChain] = useState(false);
  
  // State to track if we're in a post-reset state
  const [isCleanReset, setIsCleanReset] = useState(false);
  // Handle deep link chain selection on initial load
  const [appliedDeepLink, setAppliedDeepLink] = useState(false);
  
  // Define hasWallets early to avoid initialization errors
  const hasWallets = userWallets && userWallets.length > 0;
  
  // Initialize Telegram WebApp and process deep links
  useEffect(() => {
    // Check if we're in Telegram
    if (isTelegramWebApp()) {
      const webApp = initTelegramWebApp();
      
      // If we have start parameters, process them
      if (webApp && webApp.initDataUnsafe && webApp.initDataUnsafe.start_param) {
        const startParam = webApp.initDataUnsafe.start_param;
        console.log("Telegram startParam detected:", startParam);
        
        // Handle deep link parameters from Telegram and get success status
        const success = handleTelegramParams(startParam);
        
        // If we successfully handled params and there's a token address, we'll navigate to swap later
        // (We don't navigate here directly - we'll let the routing system handle it)
        if (success) {
          console.log("Successfully processed Telegram deep link parameters");
        }
      }
    } else {
      // For web version, check if we need to process the current URL
      // We only do this for deep link paths that aren't handled by the router
      const path = window.location.pathname;
      if (!path.startsWith('/chain/')) {
        handleUniversalDeepLink(path);
      }
    }
  }, []);
  
  // Handle deep link chain selection on initial load
  useEffect(() => {
    // Only run once per app session to prevent infinite loops
    if (appliedDeepLink) return;
    
    // Check for deep link chain selection in session storage
    const deepLinkChain = sessionStorage.getItem('deep_link_chain');
    
    if (deepLinkChain && walletInitialized && authInitialized && 
        currentChain?.key !== deepLinkChain && hasWallets) {
      try {
        console.log(`Applying deep link chain selection: ${deepLinkChain}`);
        
        // Mark as applied before the actual switch to prevent re-triggering
        setAppliedDeepLink(true);
        
        // Clear the deep link from storage
        sessionStorage.removeItem('deep_link_chain');
        
        // Switch to the specified network
        switchNetwork(deepLinkChain);
      } catch (error) {
        console.error('Error applying deep link chain selection:', error);
        // Clean up even on error
        sessionStorage.removeItem('deep_link_chain');
      }
    } else if (hasWallets && walletInitialized && authInitialized) {
      // If we have wallets but no deep link, mark as applied to prevent future checks
      setAppliedDeepLink(true);
    }
  }, [walletInitialized, authInitialized, userWallets, currentChain, switchNetwork, appliedDeepLink, hasWallets]);

  // Handle Telegram deep link navigation after wallet is ready
  useEffect(() => {
    // Check if we need to navigate to swap for Telegram
    const needsTelegramSwapNav = sessionStorage.getItem('telegram_needs_swap_navigation') === 'true';
    const hasSwapToken = sessionStorage.getItem('deep_link_swap_token');
    
    if (needsTelegramSwapNav && hasSwapToken && hasWallets && walletInitialized && authInitialized) {
      console.log('Telegram: Navigating to swap page after wallet initialization');
      
      // Clear the navigation flag
      sessionStorage.removeItem('telegram_needs_swap_navigation');
      
      // For Telegram, we need to ensure we're at the swap route
      // Use a timeout to ensure all components are ready
      setTimeout(() => {
        if (window.location.pathname !== '/swap') {
          window.location.pathname = '/swap';
        }
      }, 500);
    }
  }, [hasWallets, walletInitialized, authInitialized]);
  
  // Apply theme on app initialization
  useEffect(() => {
    // Get theme from localStorage
    try {
      const storedSettings = localStorage.getItem('bitdogSettings');
      if (storedSettings) {
        const settings = JSON.parse(storedSettings);
        const theme = settings.theme || 'dark';
        
        // Apply theme to document
        document.documentElement.setAttribute('data-theme', theme);
        
        // Update meta theme-color for mobile devices
        if (theme === 'dark') {
          document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#121212');
        } else {
          document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#f8f9fa');
        }
      } else {
        // Default to dark theme
        document.documentElement.setAttribute('data-theme', 'dark');
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#121212');
      }
    } catch (error) {
      console.error('Error applying theme:', error);
      // Fallback to dark theme
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  }, []);

  // Modified useEffect for reset detection - use a more definitive approach
  useEffect(() => {
    // Check for the clean reset flag
    const cleanReset = sessionStorage.getItem('clean_reset') === 'true';
    
    if (cleanReset) {
      console.log("Clean reset detected, ensuring welcome page display");
      
      // CRITICAL: Clear the reset flag IMMEDIATELY to prevent re-detection
      sessionStorage.removeItem('clean_reset');
      
      // CRITICAL: Also clear any recently created wallet flag to prevent conflicts
      sessionStorage.removeItem('bitdog_wallet_created');
      
      // Set state to indicate reset (one-time only)
      setIsCleanReset(true);
      
      // If we're not at the root path, force navigation there
      if (window.location.pathname !== '/' || window.location.search) {
        window.history.replaceState(null, '', '/');
      }
    }
    
    // Remove these problematic parameters if present
    if (window.location.search.includes('force_reset=') || 
        window.location.search.includes('reset=') ||
        window.location.search.includes('safari_reset=')) {
      
      console.log("Removing problematic query parameters");
      window.history.replaceState(null, '', window.location.pathname);
    }
    
    // Mobile-specific: Special handling for expired sessions
    const checkMobileSession = () => {
      // Only run this on mobile devices
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (!isMobile) return;
      
      console.log("Mobile device detected, checking for session restoration");
      const hasSessionExpired = localStorage.getItem('bitdog_session_expired') === 'true';
      
      if (hasSessionExpired && hasStoredWallets) {
        console.log("Mobile: Found expired session with stored wallets - will handle with auto-auth");
        // Do NOT remove the flag here, it will be removed after successful authentication
      }
    };
    
    checkMobileSession();
  }, [hasStoredWallets]);
  
  // CRITICAL: Add this effect specifically to transition from isCleanReset to normal state
  // This ensures we only show the welcome page ONCE after reset
  useEffect(() => {
    if (isCleanReset && userWallets && userWallets.length > 0) {
      console.log("New wallet created after reset, clearing reset state");
      setIsCleanReset(false);
    }
  }, [isCleanReset, userWallets]);
  
  // Safe cleanup of any leftover reset parameters
  useEffect(() => {
    if (!walletInitialized || !authInitialized) return;
    
    // Safety check for any legacy reset flags
    const legacyResetFlag = sessionStorage.getItem('bitdog_fresh_reset');
    if (legacyResetFlag) {
      console.log("Clearing legacy reset flag");
      sessionStorage.removeItem('bitdog_fresh_reset');
    }
    
    // Check if we have any reset query parameters and clean them
    if (window.location.search.includes('reset=') || 
        window.location.search.includes('force_reset=') ||
        window.location.search.includes('safari_reset=')) {
      
      console.log("Cleaning up reset URL parameters");
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [walletInitialized, authInitialized]);

  useEffect(() => {
    debugAuthentication(isAuthenticated, hasStoredWallets, isEncrypted);
  }, [isAuthenticated, hasStoredWallets, isEncrypted]);

  // CRITICAL: Only continue when BOTH initialization is complete AND minimum time has passed
  const showContent = (walletInitialized && authInitialized && minTimeElapsed);
  
  // If not ready to show content, show loading screen
  if (!walletInitialized || !authInitialized) {
    return <WalletLoading message={t('welcome.steps.welcomeIntro')} minLoadTime={1000} />;
  }

  /* 
   * IMPROVED ROUTING LOGIC:
   * 1. hasWallets - User has loaded wallets visible now
   * 2. isPendingAuth - User has wallets but needs to authenticate first
   * 3. isCleanReset - App just went through a reset and should show welcome page
   */
  // hasWallets is already defined above

  // IMPROVED: Check for password-deferred (auto-security) users separately
  const hasDeferredPassword = passwordDeferred === true;

  // IMPROVED: Consider "expired" mobile sessions as pending auth only if user has a password
  const hasMobileExpiredSession = localStorage.getItem('bitdog_session_expired') === 'true';

  // For normal users with password: show auth prompt for encrypted wallets or expired sessions
  // For auto-security users: the AuthWrapper will handle auto-authentication
  const isPendingAuth = (hasStoredWallets && isEncrypted && !isCleanReset) || 
                       (hasMobileExpiredSession && hasStoredWallets);

  // Check if we're coming from wallet creation
  const isPostWalletCreation = sessionStorage.getItem('bitdog_wallet_created') === 'true';

  // Completely rewrite the routing logic based on priority:
  
  // 1. If we have wallets, ALWAYS show wallet routes regardless of any reset flags
  if (hasWallets) {
    console.log("Wallet detected - showing wallet interface");
    return (
      <ThemeInitializer>
        <AuthWrapper>
          <Router>
            <div style={{ height: '100%' }}>
              <Routes>
                {/* Deep link routes with specific suspense fallbacks */}
                <Route path="/chain/:chainKey" element={
                  <Suspense fallback={<ChainFallback />}>
                    <ChainRedirect />
                  </Suspense>
                } />
                <Route path="/chain/:chainKey/:tokenAddress" element={
                  <Suspense fallback={<SwapFallback />}>
                    {isTelegramWebApp() ? <TelegramSwapRedirect /> : <SwapRedirect />}
                  </Suspense>
                } />
                
                {/* Add the Install PWA Guide route */}
                <Route path="/settings/install-guide" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <InstallPWAGuide />
                  </Suspense>
                } />
                
                {/* Existing routes with general fallback */}
                <Route path="/" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <Navigate to="/wallet" />
                  </Suspense>
                } />
                <Route path="/wallet" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/transactions" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/swap" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/browser" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/settings" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="*" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <Navigate to="/wallet" />
                  </Suspense>
                } />
              </Routes>
            </div>
          </Router>
        </AuthWrapper>
      </ThemeInitializer>
    );
  }
  // 2. If we're in post-wallet creation state, also show wallet routes
  else if (isPostWalletCreation) {
    console.log("Post wallet creation state detected, forcing wallet routes");
    sessionStorage.removeItem('bitdog_wallet_created');
    
    return (
      <ThemeInitializer>
        <AuthWrapper>
          <Router>
            <div style={{ height: '100%' }}>
              <Routes>
                {/* Deep link routes with specific suspense fallbacks */}
                <Route path="/chain/:chainKey" element={
                  <Suspense fallback={<ChainFallback />}>
                    <ChainRedirect />
                  </Suspense>
                } />
                <Route path="/chain/:chainKey/:tokenAddress" element={
                  <Suspense fallback={<SwapFallback />}>
                    <SwapRedirect />
                  </Suspense>
                } />
                
                {/* Add the Install PWA Guide route */}
                <Route path="/settings/install-guide" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <InstallPWAGuide />
                  </Suspense>
                } />

                <Route path="/install-guide" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <InstallPWAGuide />
                  </Suspense>
                } />
                
                {/* Existing routes with general fallback */}
                <Route path="/" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <Navigate to="/wallet" />
                  </Suspense>
                } />
                <Route path="/wallet" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/transactions" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/swap" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/settings" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="*" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <Navigate to="/wallet" />
                  </Suspense>
                } />
              </Routes>
            </div>
          </Router>
        </AuthWrapper>
      </ThemeInitializer>
    );
  }
  // 3. If we're in clean reset state, show welcome page
  else if (isCleanReset) {
    console.log("Clean reset with no wallets - showing welcome page");
    return (
      <ThemeInitializer>
        <AuthWrapper>
          <Router>
            <div style={{ height: '100%' }}>
              <Routes>
                {/* Deep link routes with specific suspense fallbacks */}
                <Route path="/chain/:chainKey" element={
                  <Suspense fallback={<ChainFallback />}>
                    <ChainRedirect />
                  </Suspense>
                } />
                <Route path="/chain/:chainKey/:tokenAddress" element={
                  <Suspense fallback={<SwapFallback />}>
                    <SwapRedirect />
                  </Suspense>
                } />
                
                {/* Existing routes with welcome fallback */}
                <Route path="*" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <WelcomePage />
                  </Suspense>
                } />
              </Routes>
            </div>
          </Router>
        </AuthWrapper>
      </ThemeInitializer>
    );
  }
  // 4. If we have pending auth (encrypted wallets that need password), show wallet routes
  else if (isPendingAuth) {
    console.log("Pending auth - showing wallet interface with auth prompt");
    return (
      <ThemeInitializer>
        <AuthWrapper>
          <Router>
            <div style={{ height: '100%' }}>
              <Routes>
                {/* Deep link routes with specific suspense fallbacks */}
                <Route path="/chain/:chainKey" element={
                  <Suspense fallback={<ChainFallback />}>
                    <ChainRedirect />
                  </Suspense>
                } />
                <Route path="/chain/:chainKey/:tokenAddress" element={
                  <Suspense fallback={<SwapFallback />}>
                    <SwapRedirect />
                  </Suspense>
                } />
                
                {/* Add the Install PWA Guide route */}
                <Route path="/settings/install-guide" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <InstallPWAGuide />
                  </Suspense>
                } />
                
                {/* Existing routes with general fallback */}
                <Route path="/" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <Navigate to="/wallet" />
                  </Suspense>
                } />
                <Route path="/wallet" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/transactions" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/swap" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="/settings" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <TabContainer />
                  </Suspense>
                } />
                <Route path="*" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <Navigate to="/wallet" />
                  </Suspense>
                } />
              </Routes>
            </div>
          </Router>
        </AuthWrapper>
      </ThemeInitializer>
    );
  }
  // 5. Default case: no wallets, no reset, no pending auth - show welcome page
  else {
    console.log("No wallets - showing welcome page");
    return (
      <ThemeInitializer>
        <AuthWrapper>
          <Router>
            <div style={{ height: '100%' }}>
              <Routes>
                {/* Use our new redirect components for deep links */}
                <Route path="/chain/:chainKey" element={
                  <Suspense fallback={<ChainFallback />}>
                    <ChainParamRedirect />
                  </Suspense>
                } />
                <Route path="/chain/:chainKey/:tokenAddress" element={
                  <Suspense fallback={<SwapFallback />}>
                    <TokenParamRedirect />
                  </Suspense>
                } />
                
                {/* Existing routes with welcome fallback */}
                <Route path="/" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <WelcomePage />
                  </Suspense>
                } />
                <Route path="*" element={
                  <Suspense fallback={<WelcomeFallback />}>
                    <Navigate to="/" />
                  </Suspense>
                } />
              </Routes>
            </div>
          </Router>
        </AuthWrapper>
      </ThemeInitializer>
    );
  }
}

// Export with auth provider wrapper and splash screen
export default function AppWithProviders() {
  const { t } = useTranslation(); // Add translation hook
  // State to control splash screen visibility
  const [showSplash, setShowSplash] = useState(true);
  
  // Effect to hide splash screen after exactly 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 1000); // 1 second
    
    // Check if this is a return visit within the same session
    const hasSeenSplash = sessionStorage.getItem('has_seen_splash');
    if (hasSeenSplash) {
      // Skip splash screen for return visits in the same session
      setShowSplash(false);
      clearTimeout(timer);
    } else {
      // Mark that user has seen splash screen this session
      sessionStorage.setItem('has_seen_splash', 'true');
    }
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <>
      {/* Splash screen overlay */}
      {showSplash && (
        <div className="splash-screen-overlay">
          <WalletLoading message={t('welcome.steps.welcomeIntro')} minLoadTime={1000} />
        </div>
      )}
      
      {/* Main app */}
      <App />
      
      {/* Splash screen styling */}
      <style>{`
        .splash-screen-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
          background-color: var(--app-background);
          transition: opacity 0.3s ease;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
}